import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import supportTicketService from '../../services/supportTicketService';

/**
 * RaiseIssueChat — chat-style "Raise an Issue" Dialog.
 *
 * Conversational flow (matches `support-ticket-chat-prototype.html` from
 * frontend/public/prototype/):
 *
 *   1. Bot asks "What's the issue about?" — user picks a category chip.
 *   2. Per-category follow-up (report type / lead+bank / credits sub /
 *      free-text only).
 *   3. Note + (future) attachment.
 *   4. Bot shows a review card; user submits.
 *   5. Success: ticket ref shown + "View My Issues" CTA.
 *
 * Tapping any earlier user bubble re-asks the question from that point;
 * later bubbles are visually staled.
 *
 * Data flow: all categories + searchable pickers come from the PR 4
 * lookup endpoints. The submit hits POST /api/support/tickets.
 *
 * UI rules: PrimeReact only (per project ui-ux skill). No new CSS files
 * — all styling via inline style + PrimeFlex utility classes.
 */

const CATEGORIES_FALLBACK = []; // Filled in from API; empty here so a load-failure shows the "try again" state.

const STEP_ORDER = ['category', 'subdetail', 'note'];

const RaiseIssueChat = ({ visible, onHide, onNavigateMyIssues }) => {
  const toastRef = useRef(null);
  const scrollRef = useRef(null);

  // ── Conversation state ──────────────────────────────────────────────
  const [messages, setMessages] = useState([]);
  const [step, setStep] = useState('category');     // current open step
  const [activeReplyZone, setActiveReplyZone] = useState('chips:categories');
  const [busy, setBusy] = useState(false);          // disables reply zone during async
  const [botTyping, setBotTyping] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── Answer state ────────────────────────────────────────────────────
  const [categories, setCategories] = useState(CATEGORIES_FALLBACK);
  const [answers, setAnswers] = useState({
    category: null,         // {key, label, description, icon, routing_label}
    reportType: null,       // {value, label, meta}
    leadId: null,           // {value, lead_id, label, meta}
    bankId: null,           // {value, label, meta}
    creditSub: null,        // 'purchase' | 'deduction'
    note: '',
  });

  // ── Submit result ───────────────────────────────────────────────────
  const [ticketRef, setTicketRef] = useState(null);

  // ── Reset on open ───────────────────────────────────────────────────
  useEffect(() => {
    if (!visible) return;
    resetConversation();
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // ── Auto-scroll chat to bottom on new message ───────────────────────
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, botTyping]);

  // ── Helpers ─────────────────────────────────────────────────────────

  const resetConversation = () => {
    setMessages([]);
    setStep('category');
    setActiveReplyZone('chips:categories');
    setAnswers({ category: null, reportType: null, leadId: null, bankId: null, creditSub: null, note: '' });
    setTicketRef(null);
    setBusy(false);
    setSubmitting(false);
    // Bot's opening line is queued by loadCategories() (after the API completes
    // so we don't show chips before the data is in).
  };

  const loadCategories = async () => {
    setBotTyping(true);
    try {
      const list = await supportTicketService.getCategories();
      setCategories(list);
    } catch (e) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Could not load categories',
        detail: 'Please try again in a moment.',
        life: 4000,
      });
    }
    setBotTyping(false);
    pushBot("Hi 👋  I'm here to help raise your issue. What's it about?");
  };

  const pushBot = (text) => {
    setMessages((prev) => [...prev, { id: nextId(), from: 'bot', text }]);
  };

  const pushUser = (step, label) => {
    setMessages((prev) => [...prev, { id: nextId(), from: 'user', step, label, staled: false }]);
  };

  /** Tap a prior user bubble → re-ask from that step. Stale downstream. */
  const editStep = (stepKey) => {
    if (submitting) return;
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.from === 'user' && m.step === stepKey);
      if (idx === -1) return prev;
      // Keep everything up to and including the bot question that preceded
      // this answer (the question is messages[idx-1] if it's a bot bubble).
      // Stale the target bubble's content + drop everything after.
      const kept = prev.slice(0, idx).map((m) => ({ ...m }));
      return [...kept];
    });
    // Reopen that step's reply zone.
    setStep(stepKey);
    rerunStep(stepKey);
  };

  /** Re-emit the bot question for a given step. */
  const rerunStep = async (stepKey) => {
    setBotTyping(true);
    await delay(400);
    setBotTyping(false);
    if (stepKey === 'category') {
      pushBot("What's it about?");
      setActiveReplyZone('chips:categories');
    } else if (stepKey === 'subdetail') {
      askSubdetail(answers.category);
    } else if (stepKey === 'note') {
      askNote(answers.category);
    }
  };

  // ── Category chosen ─────────────────────────────────────────────────

  const onPickCategory = (cat) => {
    if (cat.enabled === false) return;
    setAnswers((a) => ({ ...a, category: cat }));
    pushUser('category', cat.label);
    setStep('subdetail');
    askSubdetail(cat);
  };

  const askSubdetail = async (cat) => {
    setBotTyping(true);
    await delay(500);
    setBotTyping(false);

    if (cat.key === 'borrower_reports') {
      pushBot('Which report is the issue with?');
      setActiveReplyZone('search:reportType');
    } else if (cat.key === 'offer_generation') {
      pushBot('Is there a specific lead you want to flag? (Skip if it affects many.)');
      setActiveReplyZone('search:lead');
    } else if (cat.key === 'credits_issue') {
      pushBot("What kind of credit issue?  The central team will handle this.");
      setActiveReplyZone('chips:creditSub');
    } else {
      // 'others' or any other enabled category → jump straight to note
      pushBot('Tell us what happened in your own words.');
      setActiveReplyZone('text:note:required');
      setStep('note');
    }
  };

  // ── Subdetail answered ──────────────────────────────────────────────

  const onPickReportType = (opt) => {
    setAnswers((a) => ({ ...a, reportType: opt }));
    // Defensive label resolution — PrimeReact Dropdown's onChange may pass
    // the bare optionLabel string (the `label` field) OR the full option
    // object depending on its config. Handle both.
    pushUser('subdetail', resolveLabel(opt, 'Selected report'));
    setStep('note');
    askNote(answers.category);
  };

  const onPickLead = async (opt) => {
    setAnswers((a) => ({ ...a, leadId: opt }));
    pushUser('subdetail', opt
      ? resolveLabel(opt, 'Selected lead')
      : 'No specific lead — affects many');
    // After lead, ask for bank.
    setBotTyping(true);
    await delay(400);
    setBotTyping(false);
    pushBot('And which bank?');
    setActiveReplyZone('search:bank');
    setStep('subdetail-bank');
  };

  const onPickBank = (opt) => {
    setAnswers((a) => ({ ...a, bankId: opt }));
    pushUser('subdetail-bank', opt
      ? resolveLabel(opt, 'Selected bank')
      : 'No specific bank — affects all');
    setStep('note');
    askNote(answers.category);
  };

  const onPickCreditSub = (key, label) => {
    setAnswers((a) => ({ ...a, creditSub: key }));
    pushUser('subdetail', label);
    setStep('note');
    askNote(answers.category);
  };

  // ── Note step ───────────────────────────────────────────────────────

  const askNote = async (cat) => {
    setBotTyping(true);
    await delay(400);
    setBotTyping(false);
    if (cat?.key === 'others') {
      // Already asked the "tell us what happened" prompt — but we still want
      // it here in case of edit.
      pushBot('Tell us what happened.');
      setActiveReplyZone('text:note:required');
    } else {
      pushBot("Anything to add as a note? (You can skip.)");
      setActiveReplyZone('text:note:optional');
    }
  };

  const onSubmitNote = (text) => {
    setAnswers((a) => ({ ...a, note: text.trim() }));
    pushUser('note', text.trim() ? text.trim() : '(no note)');
    setStep('review');
    showReview();
  };

  // ── Review + submit ─────────────────────────────────────────────────

  const showReview = async () => {
    setBotTyping(true);
    await delay(500);
    setBotTyping(false);
    pushBot("Here's what I'll send — submit when you're happy.");
    setActiveReplyZone('review');
  };

  const doSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setBusy(true);

    try {
      const payload = buildPayload();
      const ticket = await supportTicketService.createTicket(payload);
      setTicketRef(ticket.ticket_ref);
      pushUser('submit', 'Submit ✓');
      await delay(400);
      pushBot(`Done. Your reference is ${ticket.ticket_ref}.`);
      setActiveReplyZone('success');
    } catch (e) {
      const msg = e?.response?.data?.message || 'Could not submit. Please try again.';
      toastRef.current?.show({
        severity: 'error',
        summary: 'Submit failed',
        detail: msg,
        life: 5000,
      });
    } finally {
      setSubmitting(false);
      setBusy(false);
    }
  };

  const buildPayload = () => {
    const payload = {
      category: answers.category.key,
      note: answers.note || undefined,
    };
    if (answers.category.key === 'borrower_reports' && answers.reportType) {
      // PrimeReact Dropdown may hand back a bare string OR an object —
      // pull the value field if present, else use the string itself.
      payload.subcategory = answers.reportType.value || answers.reportType;
      payload.context_payload = { report_type: payload.subcategory };
    }
    if (answers.category.key === 'offer_generation') {
      if (answers.leadId?.lead_id) payload.context_lead_id = answers.leadId.lead_id;
      if (answers.bankId?.value)   payload.context_bank_id = answers.bankId.value;
    }
    if (answers.category.key === 'credits_issue' && answers.creditSub) {
      payload.subcategory = answers.creditSub;
    }
    return payload;
  };

  // ── Render ──────────────────────────────────────────────────────────

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      modal
      dismissableMask
      closable={false}
      showHeader={false}
      contentStyle={{ padding: 0, background: 'var(--surface-50)' }}
      style={{ width: '480px', maxWidth: '95vw', borderRadius: 14, overflow: 'hidden' }}
      breakpoints={{ '720px': '100vw' }}
    >
      <Toast ref={toastRef} position="top-right" />

      {/* Custom chat header (PrimeReact's default header is hidden via
          showHeader={false} so we can draw the blue WhatsApp-style bar). */}
      <div
        className="flex align-items-center gap-3 px-3 py-2"
        style={{ background: 'var(--primary-color)', color: '#fff' }}
      >
        {renderHeader(onHide)}
      </div>

      {/* Chat stream */}
      <div
        ref={scrollRef}
        style={{
          padding: '1rem 0.85rem',
          maxHeight: '54vh',
          minHeight: '300px',
          overflowY: 'auto',
        }}
      >
        <div className="flex justify-content-center mb-3">
          <span
            className="text-xs font-semibold text-color-secondary px-2 py-1 border-round-pill"
            style={{ background: 'rgba(255,255,255,.85)', letterSpacing: '.06em', textTransform: 'uppercase' }}
          >
            Today
          </span>
        </div>

        {messages.map((m) =>
          m.from === 'bot' ? (
            <BotBubble key={m.id} text={m.text} />
          ) : (
            <UserBubble
              key={m.id}
              label={m.label}
              staled={m.staled}
              onClick={() => !submitting && m.step && editStep(m.step)}
            />
          ),
        )}

        {botTyping && <TypingBubble />}
      </div>

      {/* Reply zone — chip grid / picker / textarea / review buttons */}
      <ReplyZone
        zone={activeReplyZone}
        busy={busy}
        submitting={submitting}
        categories={categories}
        answers={answers}
        ticketRef={ticketRef}
        onPickCategory={onPickCategory}
        onPickReportType={onPickReportType}
        onPickLead={onPickLead}
        onPickBank={onPickBank}
        onPickCreditSub={onPickCreditSub}
        onSubmitNote={onSubmitNote}
        onSubmit={doSubmit}
        onEditReview={() => editStep('note')}
        onClose={onHide}
        onViewMyIssues={() => {
          if (onNavigateMyIssues) onNavigateMyIssues();
          onHide();
        }}
      />
    </Dialog>
  );
};

// ── Subcomponents ────────────────────────────────────────────────────

const renderHeader = (onClose) => (
  <div className="flex align-items-center gap-3">
    <div
      className="flex align-items-center justify-content-center border-circle"
      aria-hidden="true"
      style={{
        width: 38, height: 38,
        background: 'rgba(255,255,255,.2)',
        border: '2px solid rgba(255,255,255,.35)',
        color: '#fff',
      }}
    >
      <i className="pi pi-comment"></i>
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-base font-bold" style={{ color: '#fff', letterSpacing: '-.005em' }}>
        Interestng Support
      </div>
      <div className="flex align-items-center gap-2 text-xs" style={{ color: 'rgba(255,255,255,.85)' }}>
        <span
          aria-hidden="true"
          style={{
            width: 7, height: 7, borderRadius: '50%',
            background: '#34d399',
            boxShadow: '0 0 0 2px rgba(52,211,153,.3)',
          }}
        ></span>
        <span>Online · typically replies in a few hours</span>
      </div>
    </div>
    <Button
      icon="pi pi-times"
      onClick={onClose}
      aria-label="Close"
      className="p-button-rounded p-button-text"
      style={{ color: '#fff', width: 32, height: 32 }}
    />
  </div>
);

const BotBubble = ({ text }) => (
  <div className="flex justify-content-start mb-2 fadeIn">
    <div
      className="p-3 border-round shadow-1"
      style={{
        background: '#fff',
        color: 'var(--text-color)',
        maxWidth: '88%',
        borderRadius: '18px 18px 18px 4px',
        fontSize: '.9rem',
        lineHeight: 1.45,
      }}
    >
      {text}
    </div>
  </div>
);

const UserBubble = ({ label, staled, onClick }) => (
  <div className="flex justify-content-end mb-2 fadeIn">
    <div
      onClick={onClick}
      className="p-3 border-round shadow-1 cursor-pointer"
      style={{
        background: staled ? 'var(--text-color-secondary)' : 'var(--primary-color)',
        color: '#fff',
        maxWidth: '88%',
        borderRadius: '18px 18px 4px 18px',
        fontSize: '.9rem',
        lineHeight: 1.45,
        opacity: staled ? 0.55 : 1,
        transition: 'opacity .15s ease',
      }}
      role={onClick ? 'button' : undefined}
      title={onClick ? 'Tap to edit this answer' : undefined}
    >
      <div>{label}</div>
      {!staled && onClick && (
        <div className="flex align-items-center gap-1 mt-1" style={{ fontSize: '.65rem', opacity: 0.75 }}>
          <i className="pi pi-pencil" style={{ fontSize: '.6rem' }} aria-hidden="true"></i>
          <span>tap to edit</span>
        </div>
      )}
    </div>
  </div>
);

const TypingBubble = () => (
  <div className="flex justify-content-start mb-2">
    <div
      className="px-3 py-2 border-round shadow-1 flex align-items-center gap-2"
      style={{ background: '#fff', borderRadius: '18px 18px 18px 4px' }}
    >
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--text-color-secondary)', opacity: 0.5 }}></span>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--text-color-secondary)', opacity: 0.7 }}></span>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--text-color-secondary)', opacity: 1 }}></span>
    </div>
  </div>
);

// ── Reply zone (changes per step) ────────────────────────────────────

const ReplyZone = ({
  zone, busy, submitting, categories, answers, ticketRef,
  onPickCategory, onPickReportType, onPickLead, onPickBank, onPickCreditSub,
  onSubmitNote, onSubmit, onEditReview, onClose, onViewMyIssues,
}) => {
  return (
    <div
      style={{
        background: '#fff',
        borderTop: '1px solid var(--surface-border)',
        padding: '0.85rem',
        position: 'sticky',
        bottom: 0,
      }}
    >
      {zone === 'chips:categories' && (
        <CategoryChips categories={categories} onPick={onPickCategory} disabled={busy} />
      )}
      {zone === 'search:reportType' && (
        <ReportTypePicker onPick={onPickReportType} disabled={busy} />
      )}
      {zone === 'search:lead' && (
        <LeadPicker onPick={onPickLead} onSkip={() => onPickLead(null)} disabled={busy} />
      )}
      {zone === 'search:bank' && (
        <BankPicker onPick={onPickBank} onSkip={() => onPickBank(null)} disabled={busy} />
      )}
      {zone === 'chips:creditSub' && (
        <CreditSubChips onPick={onPickCreditSub} disabled={busy} />
      )}
      {(zone === 'text:note:optional' || zone === 'text:note:required') && (
        <NoteInput
          required={zone === 'text:note:required'}
          onSubmit={onSubmitNote}
          disabled={busy}
        />
      )}
      {zone === 'review' && (
        <ReviewActions answers={answers} onSubmit={onSubmit} onEdit={onEditReview} submitting={submitting} />
      )}
      {zone === 'success' && (
        <SuccessActions ticketRef={ticketRef} onClose={onClose} onViewMyIssues={onViewMyIssues} />
      )}
    </div>
  );
};

const CategoryChips = ({ categories, onPick, disabled }) => (
  <div className="flex flex-wrap gap-2">
    {categories.length === 0 ? (
      <div className="text-sm text-color-secondary">Loading…</div>
    ) : (
      categories.map((c) => (
        <Button
          key={c.key}
          onClick={() => onPick(c)}
          disabled={disabled || c.enabled === false}
          className={`p-button-outlined ${c.enabled === false ? 'p-disabled' : ''}`}
          style={{ borderRadius: '999px', fontSize: '.84rem', fontWeight: 600 }}
          aria-label={c.label}
        >
          <i className={`pi ${c.icon || 'pi-circle'} mr-2`} aria-hidden="true"></i>
          {c.label}
          {c.enabled === false && (
            <span
              style={{
                marginLeft: '.4rem', fontSize: '.6rem', background: 'var(--surface-200)',
                padding: '.1rem .35rem', borderRadius: '999px', textTransform: 'uppercase',
                letterSpacing: '.05em', fontWeight: 700,
              }}
            >
              Soon
            </span>
          )}
        </Button>
      ))
    )}
  </div>
);

const ReportTypePicker = ({ onPick, disabled }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    supportTicketService.getReportTypes()
      .then((rows) => { if (!cancelled) setOptions(rows); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <Dropdown
      placeholder={loading ? 'Loading…' : 'Search Equifax, PAN, Employment…'}
      options={options}
      filter
      filterBy="label,meta"
      optionLabel="label"
      onChange={(e) => e.value && onPick(e.value)}
      disabled={disabled || loading}
      className="w-full"
      itemTemplate={(opt) => (
        <div className="flex align-items-center gap-2">
          <i className="pi pi-tag" aria-hidden="true"></i>
          <span>{opt.label}</span>
          {opt.meta && <span className="ml-auto text-xs text-color-secondary">{opt.meta}</span>}
        </div>
      )}
    />
  );
};

const LeadPicker = ({ onPick, onSkip, disabled }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const onFilter = useCallback(async (event) => {
    const q = event?.filter || '';
    if (q.length < 2 && options.length > 0) return; // only refetch on substantive query
    setLoading(true);
    try {
      const rows = await supportTicketService.searchLeads(q);
      setOptions(rows);
    } finally {
      setLoading(false);
    }
  }, [options.length]);

  useEffect(() => { onFilter({ filter: '' }); }, []); // initial load

  return (
    <div>
      <Dropdown
        placeholder={loading ? 'Searching…' : 'Search by lead ID, phone, or name…'}
        options={options}
        filter
        filterBy="label,meta"
        optionLabel="label"
        onFilter={onFilter}
        onChange={(e) => e.value && onPick(e.value)}
        disabled={disabled}
        className="w-full"
        itemTemplate={(opt) => (
          <div className="flex align-items-center gap-2">
            <i className="pi pi-user" aria-hidden="true"></i>
            <span>{opt.label}</span>
            {opt.meta && <span className="ml-auto text-xs text-color-secondary">{opt.meta}</span>}
          </div>
        )}
      />
      <div className="flex justify-content-end mt-2">
        <Button
          label="No specific lead →"
          onClick={onSkip}
          disabled={disabled}
          className="p-button-text p-button-sm"
          style={{ fontWeight: 700 }}
        />
      </div>
    </div>
  );
};

const BankPicker = ({ onPick, onSkip, disabled }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const onFilter = useCallback(async (event) => {
    setLoading(true);
    try {
      const rows = await supportTicketService.searchBanks(event?.filter || '');
      setOptions(rows);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { onFilter({ filter: '' }); }, []);

  return (
    <div>
      <Dropdown
        placeholder={loading ? 'Searching…' : 'Search HDFC, ICICI, Axis…'}
        options={options}
        filter
        filterBy="label,meta"
        optionLabel="label"
        onFilter={onFilter}
        onChange={(e) => e.value && onPick(e.value)}
        disabled={disabled}
        className="w-full"
        itemTemplate={(opt) => (
          <div className="flex align-items-center gap-2">
            <i className="pi pi-building" aria-hidden="true"></i>
            <span>{opt.label}</span>
            {opt.meta && <span className="ml-auto text-xs text-color-secondary">{opt.meta}</span>}
          </div>
        )}
      />
      <div className="flex justify-content-end mt-2">
        <Button
          label="No specific bank →"
          onClick={onSkip}
          disabled={disabled}
          className="p-button-text p-button-sm"
          style={{ fontWeight: 700 }}
        />
      </div>
    </div>
  );
};

const CreditSubChips = ({ onPick, disabled }) => (
  <div className="flex flex-column gap-2">
    <Button
      onClick={() => onPick('purchase', 'Payment made but credits not added')}
      disabled={disabled}
      className="p-button-outlined w-full"
      style={{ justifyContent: 'flex-start', fontSize: '.84rem', fontWeight: 600 }}
    >
      <i className="pi pi-circle-fill mr-2" style={{ fontSize: '.55rem' }} aria-hidden="true"></i>
      Payment made but credits not added
    </Button>
    <Button
      onClick={() => onPick('deduction', 'Credits deducted for failed/incomplete report')}
      disabled={disabled}
      className="p-button-outlined w-full"
      style={{ justifyContent: 'flex-start', fontSize: '.84rem', fontWeight: 600 }}
    >
      <i className="pi pi-circle-fill mr-2" style={{ fontSize: '.55rem' }} aria-hidden="true"></i>
      Credits deducted for failed/incomplete report
    </Button>
  </div>
);

const NoteInput = ({ required, onSubmit, disabled }) => {
  const [text, setText] = useState('');
  const canSend = required ? text.trim().length > 0 : true;

  return (
    <div>
      <div className="flex align-items-flex-end gap-2">
        <InputTextarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={required ? 'Tell us what happened…' : 'Add details or skip'}
          rows={1}
          autoResize
          disabled={disabled}
          className="flex-1"
          style={{ resize: 'none', minHeight: 42, maxHeight: 120, fontSize: '.88rem' }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && canSend) {
              e.preventDefault();
              onSubmit(text);
            }
          }}
        />
        <Button
          icon="pi pi-send"
          onClick={() => onSubmit(text)}
          disabled={disabled || !canSend}
          aria-label="Send"
          className="p-button-rounded"
          style={{ width: 42, height: 42, flexShrink: 0 }}
        />
      </div>
      {!required && (
        <div className="flex justify-content-end mt-2">
          <Button
            label="Skip →"
            onClick={() => onSubmit('')}
            disabled={disabled}
            className="p-button-text p-button-sm"
            style={{ fontWeight: 700 }}
          />
        </div>
      )}
    </div>
  );
};

const ReviewActions = ({ answers, onSubmit, onEdit, submitting }) => (
  <div>
    <div
      className="p-3 border-round mb-3"
      style={{ background: 'linear-gradient(135deg,#ecfdf5,#eff6ff)', border: '1.5px solid #a7f3d0' }}
    >
      <div className="font-bold text-sm text-color mb-2">Here&apos;s what I&apos;ll send:</div>
      <ReviewRow k="Category" v={answers.category?.label} />
      {answers.reportType && <ReviewRow k="Report type" v={resolveLabel(answers.reportType)} />}
      {answers.leadId && <ReviewRow k="Lead" v={resolveLabel(answers.leadId)} />}
      {answers.bankId && <ReviewRow k="Bank" v={resolveLabel(answers.bankId)} />}
      {answers.creditSub && <ReviewRow k="Issue type" v={answers.creditSub === 'purchase' ? 'Purchase issue' : 'Deduction issue'} />}
      <ReviewRow k="Note" v={answers.note || '(none)'} italic={!answers.note} />
      {answers.category?.routing_label && (
        <div className="text-xs text-color-secondary mt-2" style={{ lineHeight: 1.4 }}>
          <strong className="text-color">Going to {answers.category.routing_label}</strong>{' · '}
          You&apos;ll get email updates.
        </div>
      )}
    </div>
    <div className="flex gap-2">
      <Button
        label="Edit"
        icon="pi pi-pencil"
        onClick={onEdit}
        disabled={submitting}
        className="p-button-outlined flex-1"
        style={{ borderRadius: 999, fontWeight: 700 }}
      />
      <Button
        label={submitting ? 'Submitting…' : 'Submit'}
        icon={submitting ? 'pi pi-spinner pi-spin' : 'pi pi-check'}
        onClick={onSubmit}
        disabled={submitting}
        className="p-button-success flex-1"
        style={{ borderRadius: 999, fontWeight: 700 }}
      />
    </div>
  </div>
);

const ReviewRow = ({ k, v, italic }) => (
  <div className="flex justify-content-between align-items-flex-start gap-2 py-1" style={{ borderBottom: '1px dashed rgba(0,0,0,.05)' }}>
    <div className="text-xs text-color-secondary font-semibold" style={{ width: '6rem', flexShrink: 0 }}>{k}</div>
    <div className="text-sm text-color text-right" style={{ flex: 1, fontStyle: italic ? 'italic' : 'normal' }}>{v}</div>
  </div>
);

const SuccessActions = ({ ticketRef, onClose, onViewMyIssues }) => (
  <div>
    <div className="text-center mb-3">
      <div
        className="font-mono font-bold inline-block px-3 py-2 border-round"
        style={{
          background: '#ecfdf5',
          border: '1.5px solid #a7f3d0',
          color: '#047857',
          letterSpacing: '.02em',
        }}
      >
        {ticketRef}
      </div>
    </div>
    <div className="flex gap-2">
      <Button
        label="Done"
        onClick={onClose}
        className="p-button-outlined flex-1"
        style={{ borderRadius: 999, fontWeight: 700 }}
      />
      <Button
        label="View My Issues"
        icon="pi pi-list"
        onClick={onViewMyIssues}
        className="p-button-success flex-1"
        style={{ borderRadius: 999, fontWeight: 700 }}
      />
    </div>
  </div>
);

// ── Util ─────────────────────────────────────────────────────────────

let _id = 0;
const nextId = () => ++_id;
const delay = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Resolves a display label from whatever shape Dropdown.onChange handed
 * us. PrimeReact's contract says it returns the full option object when
 * `optionValue` is unset, but in practice we see the bare label string
 * in some configurations. Cover both.
 */
const resolveLabel = (opt, fallback = '(selected)') => {
  if (opt == null) return fallback;
  if (typeof opt === 'string') return opt || fallback;
  // Object — try the common label fields, in priority order
  return opt.label || opt.name || opt.display_name || opt.value || fallback;
};

export default RaiseIssueChat;
